import { Box, Grid } from '@mui/material';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import VerificationsToolBar from './VerificationsToolBar';
import { useUser } from '../../../hooks/useUser';
import { useEffect, useState, useMemo } from 'react';
import { updateOfficialVerification } from '../utils/data';
import useBatchedPagination from '../../../hooks/useBatchedPagination';
import {
  updateOfficial,
  getAllAgencyOfficials,
  bulkVerifyOfficials,
  getOfficial
} from '../../../networkRequest/officialsService';
import SearchableAgencyDropdown from '../../../components/SearchableAgencyDropdown';
import { BuildingIcon } from '../../../assets/icons/icons';
import '../utils/verificationStyles.css';
import useAgenciesData from '../../../networkRequest/useAgenciesData';
import { Toast } from '@components';
import { CreateInternalVerificationsColumns, OfficialWithSingleVerification } from './TableConfigs';
import { UseRowEditCalculator } from '../../../hooks/dataGridEditing/UseRowEditCalculator';
import { useRowEdit } from '../../../hooks/dataGridEditing/UseRowEdit';
import { formatVerifications } from '../utils/verificationHelpers';
import { exportFilteredDataToCSV } from '../utils/csvExport';
import { getSelectedRowIds, toRowSelectionModel } from '../../../utils/gridSelection';
import { crossAgencyVerificationRoles } from '../../../utils/authorization';
import EditNotesModal from './EditNotesModal';
import VerifyAllConfirmationDialog from './VerifyAllConfirmationDialog';
import { EventService } from '../../../networkRequest/EventService';

const InternalOfficialVerificationsTable = () => {
  const { user } = useUser();

  const [selectedAgency, setSelectedAgency] = useState<number | string | null>(() => {
    const userRole = user?.profile?.role?.name;
    const isAgencyScoped = userRole && !crossAgencyVerificationRoles.includes(userRole);
    return isAgencyScoped ? user?.profile?.agency_id ?? null : 'all';
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [bulkVerificationError, setBulkVerificationError] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [verifyAllLoading, setVerifyAllLoading] = useState(false);
  const [verifyAllConfirmationOpen, setVerifyAllConfirmationOpen] = useState(false);
  const [selectedOfficial, setSelectedOfficial] = useState<OfficialWithSingleVerification | null>(
    null
  );
  const [rowSelectionModel, setRowSelectionModel] = useState<readonly (string | number)[]>([]);
  const [notesAnchorEl, setNotesAnchorEl] = useState<HTMLElement | null>(null);
  const apiRef = useGridApiRef();

  // Officials Asynchronous Handling with useBatchedPagination hook


  const agencyId = selectedAgency ?? 'all';

  const {
    data: rawOfficials,
    loading: officialsLoading,
    error: officialsError,
    setError: setOfficialsError,
    updateItem: updateOfficialInData,
    refetch
    // @ts-ignore - the dynamic fetch function here needs to be refactored
  } = useBatchedPagination(user?.accessToken ?? '', getAllAgencyOfficials, {
    enabled: !!selectedAgency, // enabled when there is a selection (defaults to 'all')
    params: {
      agencyId: agencyId
    }
  });

  // Format the officials data to have single verifications
  const officials = useMemo(() => {
    if (!rawOfficials) return [];
    // @ts-ignore
    return formatVerifications(rawOfficials);
  }, [rawOfficials]);

  const calculateRowEdit = UseRowEditCalculator();
  const {
    processRowUpdate,
    handleProcessRowUpdateError,
    editPromiseInfo,
    setEditPromiseInfo,
    resetRowMutations,
    rowMutations,
    loading: editLoading,
    error: editError
  } = useRowEdit(calculateRowEdit);

  const {
    agencies,
    error: agenciesError,
    loading: agenciesLoading,
    setError: setAgenciesError
  } = useAgenciesData(user);

  const saveSingleVerificationEdit = async (
    officialId: number,
  ) => {
    // Update the official in the batched pagination data
    const updatedOfficial = rawOfficials.find((official) => official.id === officialId);
    if (updatedOfficial) {
      const { data: official } = await getOfficial(user?.accessToken ?? '', updatedOfficial.id)
      if (official) {
        const updatedData = {
          ...updatedOfficial,
          ...official,
        };
        updateOfficialInData(updatedData);
      }
    }
  };

  useEffect(() => {
    const updateData = async () => {
      if (!user) {
        setOfficialsError('No User Found');
        return;
      }
      // when the grid processes an edit and updates editPromiseInfo, we can trigger the networkrequest to save the update.
      if (editPromiseInfo && rowMutations) {
        const { originalRow, resolve } = editPromiseInfo;
        try {
          const verificationType = rowMutations.editedKey;

          if (
            editPromiseInfo.updatedRow[verificationType] ===
            editPromiseInfo.originalRow[verificationType]?.status
          ) {
            resolve(originalRow);
            return;
          }

          const verificationId = originalRow[verificationType]?.id;

          if (!verificationId) {
            throw new Error(
              'Cannot update verification: no verification record exists for this official.'
            );
          }

          const updatedData = {
            verification: {
              status: rowMutations.updatedValue as string
            },
            id: verificationId
          };

          const data = await updateOfficialVerification(user, updatedData);
          if (data) {
            saveSingleVerificationEdit(originalRow.id);
            setSuccessMessage(
              `Updated Official ${originalRow.first_name} ${originalRow.last_name} to ${rowMutations.updatedLabel} successfully.`
            );
            const fullRowData = {
              ...editPromiseInfo.originalRow,
              // for local officials state, each official only has the most recent verification saved, not an array.
              [verificationType]: { ...data }
            };
            resolve(fullRowData);
          }

          resetRowMutations();
          setEditPromiseInfo(null);
        } catch (error: any) {
          setOfficialsError(error.message);
          resolve(originalRow);
        }
      }
    };
    updateData();
  }, [
    apiRef,
    editPromiseInfo,
    resetRowMutations,
    rowMutations,
    setEditPromiseInfo,
    user,
    setOfficialsError
  ]);

  const handleOpenNotesModal = (
    official: OfficialWithSingleVerification,
    anchorEl: HTMLElement
  ) => {
    setSelectedOfficial(official);
    setNotesAnchorEl(anchorEl);
    setNotesModalOpen(true);
  };

  const handleCloseNotesModal = () => {
    setNotesModalOpen(false);
    setSelectedOfficial(null);
  };

  const handleSaveNotes = async (notes: string) => {
    if (!selectedOfficial || !user) return;

    const actualCurrentNotes = selectedOfficial.notes || '';

    if (notes === actualCurrentNotes) {
      handleCloseNotesModal();
      return;
    }

    try {
      const { data, error } = await updateOfficial(user.accessToken, {
        id: selectedOfficial.id,
        notes: notes
      });

      if (error) {
        throw new Error(error);
      }

      if (data) {
        // @ts-ignore
        updateOfficialInData(data);

        setSuccessMessage(
          `Notes updated for ${selectedOfficial.first_name} ${selectedOfficial.last_name}`
        );
        handleCloseNotesModal();
      }
    } catch (error: any) {
      setOfficialsError(error.message);
    }
  };

  const columns = CreateInternalVerificationsColumns(handleOpenNotesModal);

  const hasAnyError = officialsError || agenciesError || editError || bulkVerificationError;
  const errorMessages = [officialsError, agenciesError, editError, bulkVerificationError];
  const allLoading = officialsLoading || agenciesLoading || editLoading;

  const clearAllErrors = () => {
    setAgenciesError('');
    setOfficialsError('');
    setBulkVerificationError('');
  };

  const handleExport = () => {
    setExportLoading(true);
    try {
      if (user === null) {
        throw new Error('user is null');
      }
      const agencyName =
        selectedAgency === 'all'
          ? 'All Agencies'
          : agencies?.find((agency) => agency.id === selectedAgency)?.name || '';
      exportFilteredDataToCSV(apiRef, agencyName);
      EventService.recordVerificationsCsvDownload(user.accessToken, selectedAgency || 'all');
    } catch (error) {
      setOfficialsError('Failed to export CSV');
    } finally {
      setExportLoading(false);
    }
  };

  const handleVerifyAll = () => {
    if (rowSelectionModel.length === 0) {
      setOfficialsError('Please select at least one official to verify.');
      return;
    }
    setVerifyAllConfirmationOpen(true);
  };

  const handleConfirmVerifyAll = async () => {
    if (!user?.accessToken) {
      setOfficialsError('No access token available');
      return;
    }

    // Do not allow verifying all officials across all agencies - 'VERIFY ALL' button disabled when 'All Agencies' is selected
    if (selectedAgency === 'all' || selectedAgency === null) {
      setOfficialsError('Cannot verify all officials across all agencies');
      return;
    }

    setVerifyAllLoading(true);
    setVerifyAllConfirmationOpen(false);

    try {
      const officialIds = [...rowSelectionModel];
      const { data, error, errors } = await bulkVerifyOfficials(
        user.accessToken,
        selectedAgency,
        officialIds
      );

      if (error) {
        throw new Error(error);
      }

      if (data && data.length > 0) {
        setSuccessMessage(data.join(', '));
      }
      if (errors && errors.length > 0) {
        setBulkVerificationError(errors.join('\n'));
      }

      setRowSelectionModel([]);
      refetch();
    } catch (error: any) {
      setOfficialsError(error.message);
    } finally {
      setVerifyAllLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        marginTop: 2
      }}>
      <Grid
        container
        sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <SearchableAgencyDropdown
          agencies={agencies || []}
          onSelect={(agencyId) => setSelectedAgency(agencyId)}
          selectedAgencyId={selectedAgency}
          loading={agenciesLoading}
          icon={<BuildingIcon sx={{ fontSize: '16pt' }} />}
          label="Agency"
        />
      </Grid>
      {allLoading ? null : (
        <>
          {hasAnyError && (
            <Toast type="error" onClose={clearAllErrors}>
              {errorMessages.filter((msg) => msg !== '').join('\n')}
            </Toast>
          )}
          {successMessage && (
            <Toast type="success" onClose={() => setSuccessMessage('')}>
              {successMessage}
            </Toast>
          )}
        </>
      )}
      <DataGrid
        columns={columns}
        rows={officials}
        loading={allLoading}
        disableRowSelectionOnClick
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        apiRef={apiRef}
        rowSelectionModel={toRowSelectionModel(rowSelectionModel)}
        onRowSelectionModelChange={(newSelection) => {
          setRowSelectionModel(getSelectedRowIds(newSelection, apiRef));
        }}
        localeText={{
          toolbarColumns: 'Manage Columns'
        }}
        showToolbar
        slots={{
          toolbar: () => (
            <VerificationsToolBar
              onVerifyAll={handleVerifyAll}
              verifyAllLoading={verifyAllLoading}
              disableVerifyAll={selectedAgency === 'all'}
              onExport={handleExport}
              exportLoading={exportLoading}
            />
          )
        }}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton'
          }
        }}
        sx={{
          '--DataGrid-overlayHeight': '300px',
          height: '100%',
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: '600'
          },
          '& .MuiDataGrid-toolbarContainer': {
            backgroundColor: '#E3F2FD'
          },
          '& .MuiDataGrid-columnHeaderTitle, & .MuiButton-text, & [data-testid="IndeterminateCheckBoxIcon"], & [data-testid="CheckBoxIcon"], & [data-testid="CheckBoxOutlineBlankIcon"]':
          {
            color: 'primary.main'
          },
          '& .MuiButton-text.Mui-disabled': {
            color: (theme: any) => theme.palette.text.disabled
          },
          '& .MuiDataGrid-overlayWrapperInner': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%'
          }
        }}
      />
      {selectedOfficial && (
        <EditNotesModal
          open={notesModalOpen}
          onClose={handleCloseNotesModal}
          onSave={handleSaveNotes}
          currentNotes={selectedOfficial.notes || ''}
          anchorEl={notesAnchorEl}
        />
      )}
      <VerifyAllConfirmationDialog
        open={verifyAllConfirmationOpen}
        onClose={() => setVerifyAllConfirmationOpen(false)}
        onConfirm={handleConfirmVerifyAll}
        selectedCount={rowSelectionModel.length}
        loading={verifyAllLoading}
      />
    </Box>
  );
};

export default InternalOfficialVerificationsTable;
